/**
 * Web widget contact handoff: pure helpers for the Assistant tab's contact-capture settings
 * and the website contact-form intake addresses (backend: demo/lib/web_widget_contact.py,
 * GET web-chat/intake-addresses/).
 */

// The backend's built-in texts, shown as placeholders: a blank field means "use these".
export const DEFAULT_CONTACT_REQUEST_MESSAGE =
  "Leave your email below and they'll get back to you there, or keep this chat open and they'll reply here.";
export const DEFAULT_CONTACT_THANKS_MESSAGE =
  "Thanks{, name}! We've passed your request to our team — they'll reply to {email}.";

/**
 * A boolean operator config value whose default applies while it is unset. Mirrors the
 * backend (`True if value in (None, "") else bool(value)`), so e.g.
 * web_widget_contact_confirmation_email renders ON for an operator that never saved it.
 */
export const configFlag = (raw, whenUnset) => (
  raw === undefined || raw === null || raw === '' ? whenUnset : !!raw
);

// The fallback message's built-in texts (backend ChatMessage.DEFAULT_WEB_CHAT_WIDGET_FALLBACK_MESSAGE
// and _QUIET, web_widget_contact.DEFAULT_CAPTURE_HOLDING_MESSAGE). Which one a blank field sends
// depends on the settings: with the email request it drops the "right here" promise, and while the
// assistant goes quiet after handing off it doesn't offer to keep answering.
export const DEFAULT_FALLBACK_MESSAGE_QUIET =
  "Thanks for reaching out! I want to make sure you get an accurate answer, so I've passed your question " +
  "along to our team — they'll follow up with you right here.";
export const DEFAULT_FALLBACK_MESSAGE =
  `${DEFAULT_FALLBACK_MESSAGE_QUIET} Feel free to ask me anything else in the meantime.`;
export const DEFAULT_CAPTURE_FALLBACK_MESSAGE =
  "Thanks for reaching out! I want to make sure you get an accurate answer, so I've passed your question " +
  'along to our team.';

/** The built-in fallback text a blank field sends with these settings. */
export const defaultFallbackMessage = (cfg, { askForEmail }) => {
  if (askForEmail) return DEFAULT_CAPTURE_FALLBACK_MESSAGE;
  return configFlag((cfg || {}).pause_chatbot_after_handoff_web_widget, true)
    ? DEFAULT_FALLBACK_MESSAGE_QUIET
    : DEFAULT_FALLBACK_MESSAGE;
};

/**
 * The whole fallback message a visitor gets (backend holding_message_with_request, else
 * ChatMessage.web_widget_fallback_message) before it is translated into their language:
 * the operator's own text or the built-in one, then the email request when it asks for one.
 */
export const fallbackMessageSent = (cfg, { askForEmail }) => {
  const c = cfg || {};
  const opening = (c.ai_auto_reply_fallback_message_web_widget || '').trim()
    || defaultFallbackMessage(c, { askForEmail });
  if (!askForEmail) return opening;
  const request = (c.web_widget_contact_request_message || '').trim() || DEFAULT_CONTACT_REQUEST_MESSAGE;
  return `${opening}\n\n${request}`;
};

/** The addresses worth showing: enabled rows with an address, from the endpoint payload. */
export const enabledIntakeAddresses = (payload) => {
  const rows = payload && Array.isArray(payload.addresses) ? payload.addresses : [];
  return rows.filter((row) => row && row.enabled && row.address);
};

/**
 * "Send team emails to" (backend: GET/POST web-chat/contact-team/). The field takes addresses
 * separated by commas, semicolons, spaces or new lines -- the same split the backend applies.
 */
export const parseAddressList = (text) => (text || '').split(/[,;\s]+/).map((a) => a.trim()).filter(Boolean);

/** Same addresses, ignoring case, order and duplicates (is there anything to save?). */
export const sameAddressList = (a, b) => {
  const norm = (list) => Array.from(new Set(list.map((x) => x.toLowerCase()))).sort().join(',');
  return norm(a) === norm(b);
};

/**
 * The team-email rule the field edits, from the status payload: the one rule the page may
 * change in place (null when the page should send the user to Notifications instead).
 */
export const editableTeamRule = (status) => {
  if (!status || !status.editable_rule_id || !Array.isArray(status.rules)) return null;
  return status.rules.find((r) => r.id === status.editable_rule_id) || null;
};
